// "Data API tokens" on the project page of a HOSTED project (task 2026-10-05 "a REST API with
// per-project tokens"): the signed-in user creates a named token `unio_pat_...` for a script or
// pipeline that calls /api/projects/{id}/data/... over HTTP. The secret is shown ONCE, in the
// answer of POST /api/projects/{id}/tokens, and kept only in this component's state. A `read`
// token reads; a `write` token writes what its owner may write (the backend checks the owner's
// current data role at every request). A data admin also sees and revokes everybody's tokens.
import { useState } from "react";
import { api } from "../api";
import { ErrorNote, Loading, SectionTitle } from "../components/Feedback";
import { Badge, Btn, Card, Field, Input, Select } from "../components/Shared";
import { useApi } from "../useApi";

const MONO = { fontFamily: "'JetBrains Mono', monospace" };
const STATE_COLOR = { active: "green", expired: "gray", revoked: "red" };

function when(iso) {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

function Snippet({ label, children }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 12, color: "#4b5563" }}>{label}</div>
      <pre style={{ ...MONO, margin: "4px 0 0", padding: "8px 10px", background: "#f3f4f6", borderRadius: 6,
                    fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{children}</pre>
    </div>
  );
}

function TokenTable({ caption, tokens, busy, onRevoke, showOwner }) {
  return (
    <table className="responsive-table" style={{ marginTop: 16, width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <caption style={{ textAlign: "left", fontWeight: 600, padding: "4px 0" }}>{caption}</caption>
      <thead>
        <tr style={{ textAlign: "left", color: "#6b7280" }}>
          <th scope="col">Name</th>{showOwner && <th scope="col">Person</th>}<th scope="col">Token</th>
          <th scope="col">Scope</th><th scope="col">State</th><th scope="col">Expires</th>
          <th scope="col">Last used</th>
          <th scope="col"><span style={{ position: "absolute", left: -9999 }}>Actions</span></th>
        </tr>
      </thead>
      <tbody>
        {tokens.map(t => (
          <tr key={t.id} style={{ borderTop: "1px solid #e5e7eb" }}>
            <td data-label="Name" style={{ padding: "4px 0" }}>{t.name}</td>
            {showOwner && <td data-label="Person">{t.username}</td>}
            <td data-label="Token" style={MONO}>{t.prefix}…</td>
            <td data-label="Scope">{t.scope}</td>
            <td data-label="State"><Badge color={STATE_COLOR[t.state] || "gray"}>{t.state}</Badge></td>
            <td data-label="Expires">{when(t.expires_at)}</td>
            <td data-label="Last used">{when(t.last_used_at)}</td>
            <td style={{ textAlign: "right" }}>
              {t.state === "active" && (
                <Btn variant="secondary" disabled={busy} onClick={() => onRevoke(t)}>
                  Revoke<span style={{ position: "absolute", left: -9999 }}> the token {t.name}{showOwner ? ` of ${t.username}` : ""}</span>
                </Btn>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ApiTokensPanel({ projectId }) {
  const state = useApi(`/projects/${projectId}/tokens`);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("read");
  const [days, setDays] = useState("");
  const [created, setCreated] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const d = state.data;

  const create = async e => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body = { name: name.trim(), scope };
      if (days) body.valid_days = Number(days);
      setCreated(await api.post(`/projects/${projectId}/tokens`, body));
      setName("");
      state.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async token => {
    setError(null);
    setBusy(true);
    try {
      await api.del(`/projects/${projectId}/tokens/${token.id}`);
      if (created && created.token.id === token.id) setCreated(null);
      state.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Data API tokens" style={{ marginBottom: 24 }}>
      <SectionTitle>Data API tokens</SectionTitle>
      <ErrorNote error={state.error || error} />
      {state.loading && !d && <Loading />}
      {d && (
        <Card style={{ padding: "18px 22px" }}>
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "#374151" }}>
            A token lets a script or pipeline read and write this project's tables over HTTP
            (<code style={MONO}>{d.data_url}</code>) without your sign-in. It works only for this project
            and only on the data endpoints, never with more than your data role
            (<Badge color="blue">{d.my_level}</Badge>) at the moment it is used. The token is shown once;
            it stops working when it expires or is revoked, when you lose your role, or the database
            stops being hosted. At most {d.requests_per_minute} requests per minute per token.
          </p>
          <form onSubmit={create} style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <Field label="Token name" hint="What will use it, e.g. nightly-import.">
              <Input value={name} maxLength={100} required onChange={e => setName(e.target.value)} style={{ width: 220 }} />
            </Field>
            <Field label="Scope" hint={d.scopes.includes("write") ? "read, or write as your data role allows." : "Your data role reads only."}>
              <Select value={scope} required options={d.scopes} onChange={e => setScope(e.target.value)} />
            </Field>
            <Field label="Valid for (days)" hint={`1 to ${d.max_valid_days}; ${d.default_valid_days} when empty.`}>
              <Input type="number" min={1} max={d.max_valid_days} value={days}
                     onChange={e => setDays(e.target.value)} style={{ width: 120 }} />
            </Field>
            <Btn type="submit" disabled={busy || !name.trim() || !scope}>Create token</Btn>
          </form>
          {created && (
            <div role="status" style={{ marginTop: 16, padding: "12px 14px", borderRadius: 8,
                                        background: "rgba(0,150,64,0.06)", border: "1px solid rgba(0,150,64,0.35)" }}>
              <strong>Copy the token now — it is not shown again.</strong>
              <Snippet label={`Token "${created.token.name}" (${created.token.scope})`}>{created.secret}</Snippet>
              <Snippet label="curl">{created.curl}</Snippet>
              <Snippet label="Python (requests)">
                {`requests.get("${d.data_url}",\n  headers={"Authorization": "Bearer " + os.environ["UNIO_TOKEN"]}).json()`}
              </Snippet>
            </div>
          )}
          {d.mine.length > 0
            ? <TokenTable caption="Your tokens" tokens={d.mine} busy={busy} onRevoke={revoke} />
            : <p style={{ margin: "12px 0 0", fontSize: 14 }}>You have no token for this project yet.</p>}
          {d.can_admin && d.all && d.all.length > 0 && (
            <TokenTable caption="Tokens of this project" tokens={d.all} busy={busy} onRevoke={revoke} showOwner />
          )}
        </Card>
      )}
    </section>
  );
}
